import React, { useState } from 'react';
import { FaLock, FaCreditCard, FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../contexts/CreditsContext';
import paywallService from '../../services/paywallService';
import { toast } from 'react-toastify';
import './PaywallModal.css';

const PaywallModal = ({
                          isOpen,
                          onClose,
                          contentId,
                          contentType, // 'post', 'track', 'video'
                          contentTitle,
                          price,
                          onPurchaseSuccess,
                          onLoginRequired
                      }) => {
    const [loading, setLoading] = useState(false);
    const { isAuthenticated } = useAuth();
    const { creditBalance, deductCredits, refreshBalance } = useCredits();
    const navigate = useNavigate();

    if (!isOpen) return null;

    const handlePurchase = async () => {
        if (!isAuthenticated) {
            // Don't close the modal, just call login required
            if (onLoginRequired) {
                onLoginRequired();
            }
            return;
        }

        try {
            setLoading(true);

            const result = await paywallService.purchaseContent(contentType, contentId);

            if (result.success) {
                toast.success('Content purchased successfully!');
                deductCredits(price);
                await refreshBalance();

                if (onPurchaseSuccess) {
                    onPurchaseSuccess();
                }
                onClose();
            } else {
                toast.error(result.message || 'Failed to purchase content.');
            }

        } catch (error) {
            console.error('Error purchasing content:', error);
            toast.error(error.response?.data?.message || error.message || 'Failed to purchase content');
        } finally {
            setLoading(false);
        }
    };

    const handleBuyCredits = () => {
        navigate('/credits', { state: { from: window.location.pathname } });
    };

    const handleLoginClick = () => {
        // Call onLoginRequired first, then close the modal
        if (onLoginRequired) {
            onLoginRequired();
        }
        // Close the paywall modal after triggering login
        onClose();
    };

    // Handle overlay click - only close if not loading
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && !loading) {
            onClose();
        }
    };

    return (
        <div className="paywall-modal-overlay" onClick={handleOverlayClick}>
            <div className="paywall-modal-content" onClick={(e) => e.stopPropagation()}>
                <button
                    className="paywall-modal-close"
                    onClick={onClose}
                    disabled={loading}
                >
                    ×
                </button>

                <div className="paywall-modal-icon">🔒</div>
                <h2 className="paywall-modal-title">Premium Content</h2>

                <p className="paywall-modal-description">
                    This content is premium. Purchase access to unlock and enjoy.
                </p>

                <div className="paywall-modal-content-info">
                    <h3>{contentTitle}</h3>
                    <div className="paywall-modal-price">
                        <span className="price-label">Price:</span>
                        <span className="price-amount">{price} credits</span>
                    </div>
                </div>

                {isAuthenticated ? (
                    <div className="paywall-modal-credit-info">
                        <div className="credit-balance">
                            <span>Your Credits:</span>
                            <strong>{creditBalance}</strong>
                        </div>

                        {creditBalance >= price ? (
                            <button
                                className="paywall-purchase-btn"
                                onClick={handlePurchase}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>Processing...</>
                                ) : (
                                    <>Purchase Access ({price} credits)</>
                                )}
                            </button>
                        ) : (
                            <>
                                <div className="insufficient-credits-warning">
                                    <FaLock className="warning-icon" />
                                    <span>You need {price - creditBalance} more credits</span>
                                </div>
                                <button
                                    className="paywall-buy-credits-btn"
                                    onClick={handleBuyCredits}
                                >
                                    <FaCreditCard /> Buy Credits
                                </button>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="paywall-modal-auth">
                        <p>Sign in to purchase and access premium content!</p>
                        <button
                            className="paywall-login-btn"
                            onClick={handleLoginClick}
                        >
                            Get Started <FaArrowRight />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaywallModal;